/**
 * The model catalog's row actions: copy the model's full name, or assign it to
 * an agent.
 *
 * **Assign to fills in, it does not save.** Picking an agent opens the
 * Assignments page with that agent's row open and this model filled in as an
 * unsaved change. Saving stays in one place, beside the cost, the health check
 * and the bar warning, so the catalog never changes an assignment by itself.
 *
 * **Only models the install's suppliers offer can be assigned.** Copy always
 * works. Assign to is greyed out on any other model, with the reason beside it
 * in a few words ("Needs an Anthropic key"). Hovering or focusing the reason
 * gives the full sentence and, for a missing or turned-down key, the setting
 * to put it in. Both read the reason the server sent with the row, so the
 * catalog never works out availability itself.
 * Whether a model meets an agent's bar is a separate question from whether it
 * is offered, so the menu shows that as a mark per agent, never as a block.
 *
 * A sibling of the other catalog controls (`CatalogPresets`, `CostBandToggles`)
 * because it is a catalog-only control; the menu itself is the shared
 * `KebabMenu`.
 */
import { useNavigate } from 'react-router-dom'
import { CopyIconButton } from '@/components/CopyIconButton'
import { KebabMenu, Tooltip } from '@/components/ui'
import type { AdminAgentName, AppSettingsResponse, ModelPricingRow, ModelSupplierStatus, ModelUnavailableReason } from '@/lib/api'
import {
  ADMIN_AGENT_ORDER,
  AGENT_LABEL,
  barShortfalls,
  supplierOfSpec,
  supplierNameFor,
  unavailableReasonHint,
  unavailableReasonLabel,
} from '@/lib/agentModelSpec'

export interface AssignToMenuProps {
  row: ModelPricingRow
  /** The app settings response: each agent's current model and bar. Null
   * while loading or when it failed, which leaves only Copy. */
  settings: AppSettingsResponse | null
  /** The install's suppliers, for the name and key setting in the reason. */
  suppliers: readonly ModelSupplierStatus[]
}

/** The address that opens Assignments with this model filled in for `agent`. */
export function assignmentHandoffPath(agent: AdminAgentName, modelSpec: string): string {
  const q = new URLSearchParams({ agent, model: modelSpec })
  return `/admin/models/assignment?${q.toString()}`
}

/** Why this install can't use the model, in a few words, with the full
 * sentence on hover or focus. */
function UnavailableReason({
  spec,
  reason,
  suppliers,
}: {
  spec: string
  reason: ModelUnavailableReason
  suppliers: readonly ModelSupplierStatus[]
}) {
  const supplierId = supplierOfSpec(spec)
  const name = supplierNameFor(supplierId, suppliers)
  const keySetting = suppliers.find(s => s.supplier === supplierId)?.keySetting
  const label = unavailableReasonLabel(reason, supplierId, name)
  return (
    <Tooltip triggerLabel={label} align="end" portal content={unavailableReasonHint(reason, supplierId, name, keySetting)}>
      <span className="text-xs text-foreground/80 whitespace-nowrap underline decoration-dotted decoration-muted-foreground underline-offset-4 cursor-help">
        {label}
      </span>
    </Tooltip>
  )
}

export function AssignToMenu({ row, settings, suppliers }: AssignToMenuProps) {
  const navigate = useNavigate()
  const spec = row.namePattern
  const offered = row.available !== false
  // Only agents the server returned a model for: during a deployment the
  // response can lag the bundle, and an agent with no model has nothing to
  // compare against.
  const agentModels = settings?.resolved?.agentModels ?? {}
  const agents = ADMIN_AGENT_ORDER.filter(a => agentModels[a] !== undefined)

  return (
    <div className="flex items-center justify-end gap-1.5">
      <CopyIconButton value={spec} title="Copy model name" ariaLabel={`Copy ${spec}`} />
      {agents.length > 0 && (
        offered ? (
          <KebabMenu
            triggerLabel="Assign to"
            ariaLabel={`Assign ${spec} to an agent`}
            width="wide"
            portal
            heading={`Use ${spec} for…`}
            footer="Opens Assignments with this model filled in for that agent. Nothing changes until you press Save agent models."
            items={agents.map(a => {
              const now = agentModels[a]!.model.spec
              const bar = settings!.agentCapabilityRequirements?.[a]?.bar
              const fits = bar ? barShortfalls(row, bar).length === 0 : false
              return {
                key: a,
                label: AGENT_LABEL[a],
                detail: now === spec ? `now: ${now} · already using it` : `now: ${now}`,
                trailing: fits ? <span className="text-success">✓ fits</span> : undefined,
                onClick: () => navigate(assignmentHandoffPath(a, spec)),
              }
            })}
          />
        ) : (
          <span className="flex items-center gap-1.5">
            <KebabMenu
              triggerLabel="Assign to"
              triggerDisabled
              ariaLabel={`${spec} is not available on this install`}
              items={[{ key: 'none', label: 'Not available', onClick: () => {} }]}
            />
            {/* An older server sends no reason; it keeps the plain words. */}
            {row.unavailableReason
              ? <UnavailableReason spec={spec} reason={row.unavailableReason} suppliers={suppliers} />
              : <span className="text-xs text-muted-foreground whitespace-nowrap">Not available</span>}
          </span>
        )
      )}
    </div>
  )
}
